// Per-upgrade "Tell us about your existing system" form. Appears between
// MachineSection and AccessoriesSection when an upgrade is selected.
//
// Each field's selected option can:
//   • add a surchargeCents to the upgrade's price (36" Statler Retrofit)
//   • earn a discountCents trade-in credit (Breeze-track, old controller) —
//     priced via rules.ts retrofitTradeInCredits + the backend
//   • force-add a product to the cart (e.g. Win 7 → Gammill Computer XE5)
//   • block submit with a custom message ("not compatible")
//   • show a warning sentence
//
// All side-effect application (force-add, block, etc.) flows through
// useEffect watching the answers — the form itself is just rendering.

import { useEffect, useMemo } from "react";

import { SectionFrame } from "./SectionFrame";
import { useConfigurator } from "../../store";
import {
  EXISTING_SYSTEM_FORMS,
  type ExistingSystemField,
  type ExistingSystemFieldOption,
} from "../../rules";

export function ExistingSystemSection() {
  const { state, dispatch } = useConfigurator();
  const catalog = state.catalog;

  const upgradeId = useMemo(() => {
    if (!catalog || !state.selectedMachineId) return null;
    const p = catalog.byId.get(state.selectedMachineId);
    return p?.category === "upgrade" ? p.id : null;
  }, [catalog, state.selectedMachineId]);

  const form = upgradeId ? EXISTING_SYSTEM_FORMS[upgradeId] : null;
  const answers = state.existingSystemAnswers;

  // Apply force-adds when answers change. We don't auto-remove on change —
  // the customer might have a reason to keep something, and removing
  // could confuse. Force-add is one-way for v1.
  useEffect(() => {
    if (!form) return;
    for (const field of form.fields) {
      const value = answers[field.id];
      if (!value) continue;
      const opt = field.options?.find((o) => o.value === value);
      if (opt?.forceAddProductId) {
        if (!state.selectedAccessoryIds.includes(opt.forceAddProductId)) {
          dispatch({
            type: "accessory/toggle",
            id: opt.forceAddProductId,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, answers]);

  if (!form) return null;

  return (
    <SectionFrame
      number={2}
      label="Existing System"
      title="Tell us about your existing system"
    >
      <p className="border-l-[3px] border-amber pl-4 py-2 font-serif text-[15px] italic leading-relaxed text-graphite">
        {form.disclaimer}
      </p>
      <div className="flex flex-col gap-4 pt-2">
        {form.fields.map((field) => {
          const visible =
            !field.showWhen ||
            field.showWhen.equalsAnyOf.includes(
              answers[field.showWhen.fieldId] ?? "",
            );
          if (!visible) return null;
          return (
            <Field
              key={field.id}
              field={field}
              value={answers[field.id] ?? ""}
              onChange={(value) =>
                dispatch({
                  type: "existingSystem/update",
                  fieldId: field.id,
                  value,
                })
              }
            />
          );
        })}
      </div>
    </SectionFrame>
  );
}

interface FieldProps {
  field: ExistingSystemField;
  value: string;
  onChange: (value: string) => void;
}

function Field({ field, value, onChange }: FieldProps) {
  const selectedOption = field.options?.find((o) => o.value === value);
  const sideEffectNote = noteForOption(selectedOption);

  return (
    <label className="flex flex-col gap-1">
      <span className="font-sans text-[14px] font-bold uppercase tracking-tag text-steel">
        {field.label}
        {field.required && <span className="text-red-700"> *</span>}
      </span>

      {field.type === "text" && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-card border border-line bg-white px-3 py-2.5 font-sans text-base text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-amber/30"
        />
      )}

      {field.type === "dropdown" && (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-card border border-line bg-white px-3 py-2.5 font-sans text-base text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-amber/30"
        >
          <option value="">— select —</option>
          {field.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}

      {field.type === "radio" && (
        <div className="flex flex-col gap-1">
          {field.options?.map((o) => (
            <label
              key={o.value}
              className={
                "flex cursor-pointer items-center gap-2 border px-3 py-2 font-sans text-sm transition " +
                (value === o.value
                  ? "border-amber bg-amber/10 text-ink"
                  : "border-line text-steel hover:border-ink hover:text-ink")
              }
            >
              <input
                type="radio"
                name={field.id}
                value={o.value}
                checked={value === o.value}
                onChange={(e) => onChange(e.target.value)}
                className="h-4 w-4 accent-amber"
              />
              {o.label}
            </label>
          ))}
        </div>
      )}

      {sideEffectNote && (
        <p
          className={
            "mt-1 border-l-2 pl-3 font-sans text-sm leading-relaxed " +
            (sideEffectNote.tone === "error"
              ? "border-red-400 text-red-700"
              : sideEffectNote.tone === "warning"
                ? "border-amber text-ink"
                : "border-amber text-steel")
          }
        >
          {sideEffectNote.message}
        </p>
      )}
    </label>
  );
}

interface NoteFromOption {
  tone: "error" | "warning" | "info";
  message: string;
}

function noteForOption(
  option: ExistingSystemFieldOption | undefined,
): NoteFromOption | null {
  if (!option) return null;
  if (option.blockSubmit) {
    return { tone: "error", message: option.blockSubmit.message };
  }
  if (option.warning) {
    return { tone: "warning", message: option.warning };
  }
  if (option.surchargeCents) {
    return {
      tone: "info",
      message: `Adds $${(option.surchargeCents / 100).toLocaleString()} to your retrofit price.`,
    };
  }
  if (option.discountCents) {
    return {
      tone: "info",
      message: `Saves $${(option.discountCents / 100).toLocaleString()} (pending photo verification).`,
    };
  }
  return null;
}
